import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Loading from "../../Common/Loading";
import Pagination from "../../Common/Pagination";
import ProductCard from "../../Components/Cards/Card";
import MobileCollectionControls, { MobileFilterActions, MobileFilterClose, isMobileSortValue, useMobileCollectionDrawer } from "../Common/MobileCollectionControls";
import { getSubcategoryRequest } from "../../Containers/Category/Category-slice";
import {
  getCartRequest,
  getcategorywithsubcategoryRequest,
  getproductbycategoryidRequest,
  getsubcategorywithproductRequest,
  insertCartRequest,
} from "../../Containers/Home/Home-slice";
import { getcategoryRequest } from "../../Containers/Product/Product-slice";
import "../../Styles/CategoryProducts.css";
import { showSuccess } from "../../utils/Toast";
import {
  cleanNumber,
  getProductImage,
  isProductOutOfStock,
} from "../../utils/productPresentation";
import { getBuyerToken } from "../../utils/authStorage";
import { addGuestCartItem } from "../../utils/guestCart";
import { CATALOG_MENU_MODE, getCustomerSubcategories, getCustomerSubcategoryLabel } from "../../utils/catalogMenuView";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "priceLow", label: "Price Low to High" },
  { value: "priceHigh", label: "Price High to Low" },
];
const PRODUCTS_PER_PAGE = 16;

const parseInteger = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const buildCategoryProductsSearch = ({
  type,
  categoryId,
  categoryName,
  subcategoryId,
  subcategoryName,
  sortBy,
}) => {
  const params = new URLSearchParams();

  if (type === "subcategory" && subcategoryId) {
    params.set("type", "subcategory");
    params.set("subcategoryId", String(subcategoryId));
  } else {
    params.set("type", "category");
  }

  if (categoryId) {
    params.set("categoryId", String(categoryId));
  }

  if (categoryName) {
    params.set("categoryName", categoryName);
  }

  if (subcategoryName && type === "subcategory") {
    params.set("subcategoryName", subcategoryName);
  }

  if (sortBy && sortBy !== "newest" && isMobileSortValue(sortBy)) {
    params.set("sort", sortBy);
  }

  const search = params.toString();
  return search ? `?${search}` : "";
};

const buildSelectionFromLocation = (state, search) => {
  const params = new URLSearchParams(search);
  const stateType = state?.type;
  const queryType = params.get("type");
  const hasSubcategory = Boolean(
    stateType === "subcategory" || params.get("subcategoryId"),
  );
  const type =
    stateType === "subcategory" || queryType === "subcategory" || hasSubcategory
      ? "subcategory"
      : "category";

  const categoryId =
    type === "subcategory"
      ? parseInteger(
          state?.catparentid ?? state?.categoryId ?? params.get("categoryId"),
        )
      : parseInteger(state?.id ?? state?.categoryId ?? params.get("categoryId"));

  const subcategoryId =
    type === "subcategory"
      ? parseInteger(
          state?.id ?? state?.subcategoryId ?? params.get("subcategoryId"),
        )
      : parseInteger(state?.subcategoryId ?? params.get("subcategoryId"));

  return {
    type,
    categoryId,
    subcategoryId,
    categoryName: state?.categoryName || params.get("categoryName") || "",
    subcategoryName:
      state?.subcategoryName || params.get("subcategoryName") || "",
  };
};

const toggleValue = (values, nextValue) =>
  values.includes(nextValue)
    ? values.filter((value) => value !== nextValue)
    : [...values, nextValue];

function CategoryProducts({
  addcartdata,
  categoryProductsResponse,
  categoryLoading,
  categoryError,
  categorydata,
  getCartRequest,
  getcategoryRequest,
  getproductbycategoryidRequest,
  getsubcategoryRequest,
  getcategorywithsubcategoryRequest,
  hasCustomerCategoryTree,
  getsubcategorywithproductRequest,
  insertCartRequest,
  subcategories,
  subcategoryLoading,
  subcategoryError,
  subcategoryProductsData,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [availability, setAvailability] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [priceRange, setPriceRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const productsTopRef = useRef(null);
  const hasMountedPageRef = useRef(false);
  const mobileDrawer = useMobileCollectionDrawer();

  const updateMobileSort = useCallback((nextSort) => {
    setSortBy(nextSort);
    const params = new URLSearchParams(location.search);
    if (nextSort === "newest") params.delete("sort");
    else params.set("sort", nextSort);
    const nextSearch = params.toString();
    if (nextSearch !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" }, { state: location.state });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("sort");
    setSortBy(window.matchMedia("(max-width: 768px)").matches && isMobileSortValue(requested) ? requested : "newest");
  }, [location.search]);

  const selection = useMemo(
    () => buildSelectionFromLocation(location.state, location.search),
    [location.search, location.state],
  );

  useEffect(() => {
    if (!location.state || location.search) {
      return;
    }

    const normalizedSearch = buildCategoryProductsSearch({
      type: selection.type,
      categoryId: selection.categoryId,
      categoryName: selection.categoryName,
      subcategoryId: selection.subcategoryId,
      subcategoryName: selection.subcategoryName,
    });

    if (!normalizedSearch) {
      return;
    }

    navigate(
      {
        pathname: "/categoryproducts",
        search: normalizedSearch,
      },
      { replace: true, state: location.state },
    );
  }, [
    location.search,
    location.state,
    navigate,
    selection.categoryId,
    selection.categoryName,
    selection.subcategoryId,
    selection.subcategoryName,
    selection.type,
  ]);

  useEffect(() => {
    getcategoryRequest();
    getsubcategoryRequest();
  }, [getcategoryRequest, getsubcategoryRequest]);

  useEffect(() => {
    if (!hasCustomerCategoryTree) getcategorywithsubcategoryRequest();
  }, [getcategorywithsubcategoryRequest, hasCustomerCategoryTree]);

  useEffect(() => {
    if (selection.type === "subcategory" && selection.subcategoryId) {
      getsubcategorywithproductRequest(selection.subcategoryId);
      if (selection.categoryId && typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
        getproductbycategoryidRequest(selection.categoryId);
      }
      return;
    }

    if (selection.categoryId) {
      getproductbycategoryidRequest(selection.categoryId);
    }
  }, [
    getproductbycategoryidRequest,
    getsubcategorywithproductRequest,
    selection.categoryId,
    selection.subcategoryId,
    selection.type,
  ]);

  useEffect(() => {
    setSelectedBrands([]);
    setSelectedSubcategories([]);
    setAvailability("all");
    setPriceRange(null);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selection.categoryId, selection.subcategoryId, selection.type]);

  useEffect(() => {
    if (!addcartdata?.success) {
      return;
    }

    showSuccess(addcartdata.message);
    getCartRequest();
  }, [addcartdata, getCartRequest]);

  const rawProducts = useMemo(() => {
    if (selection.type === "subcategory") {
      const products = Array.isArray(subcategoryProductsData?.products)
        ? subcategoryProductsData.products
        : [];
      const categoryProducts = String(categoryProductsResponse?.category_id) === String(selection.categoryId)
        ? categoryProductsResponse?.products
        : null;
      if (typeof window === "undefined" || !window.matchMedia("(max-width: 768px)").matches || !Array.isArray(categoryProducts)) {
        return products;
      }
      const categoryMetadata = new Map(categoryProducts.map((product) => [String(product.id), product]));
      return products.map((product) => {
        const metadata = categoryMetadata.get(String(product.id));
        return metadata ? { ...product, created_at: metadata.created_at, featured: metadata.featured } : product;
      });
    }

    if (Array.isArray(categoryProductsResponse?.products)) {
      return categoryProductsResponse.products;
    }

    if (Array.isArray(categoryProductsResponse?.data)) {
      return categoryProductsResponse.data;
    }

    return [];
  }, [categoryProductsResponse, selection.categoryId, selection.type, subcategoryProductsData]);

  const displayProducts = useMemo(
    () =>
      rawProducts.map((product) => {
        const basePrice = cleanNumber(product?.price);
        const discount = cleanNumber(product?.discount);
        const currentPrice =
          discount > 0 ? basePrice * (1 - discount / 100) : basePrice;

        return {
          ...product,
          title: product?.name || "Product",
          imageUrl: getProductImage(product),
          basePrice,
          price: currentPrice,
          oldPrice: discount > 0 ? basePrice : null,
          discount,
          brandKey: String(product?.brand_id ?? product?.brand_name ?? ""),
          brandLabel:
            product?.brand_name || product?.attributes?.brand || "Unbranded",
          subcategoryKey: String(product?.sub_category_id ?? ""),
          subcategoryLabel: getCustomerSubcategoryLabel(product?.sub_category_id, product?.sub_category_name),
          categoryLabel: product?.category_name || "Category",
          isOutOfStock: isProductOutOfStock(product),
          createdAtValue: new Date(product?.created_at || 0).getTime(),
        };
      }),
    [rawProducts],
  );
  const supportsDateSort = displayProducts.length > 0 && displayProducts.every((product) => Number.isFinite(product.createdAtValue) && product.createdAtValue > 0);
  const supportsFeaturedSort = displayProducts.length > 0 && displayProducts.every((product) => product.featured !== undefined && product.featured !== null);
  const hasCategoryMetadata = String(categoryProductsResponse?.category_id) === String(selection.categoryId);

  useEffect(() => {
    if (selection.type !== "subcategory" || categoryLoading || (selection.categoryId && !hasCategoryMetadata && !categoryError) || typeof window === "undefined" || !window.matchMedia("(max-width: 768px)").matches) return;
    if ((!supportsDateSort && sortBy === "oldest") || (!supportsFeaturedSort && sortBy === "featured")) {
      updateMobileSort("newest");
    }
  }, [categoryError, categoryLoading, hasCategoryMetadata, selection.categoryId, selection.type, sortBy, supportsDateSort, supportsFeaturedSort, updateMobileSort]);

  const categoryRecord = useMemo(
    () =>
      categorydata.find(
        (item) => String(item?.id) === String(selection.categoryId),
      ),
    [categorydata, selection.categoryId],
  );

  const relatedSubcategories = useMemo(
    () =>
      subcategories.filter(
        (item) => String(item?.category_id) === String(selection.categoryId),
      ),
    [selection.categoryId, subcategories],
  );

  const currentSubcategoryRecord = useMemo(
    () =>
      relatedSubcategories.find(
        (item) => String(item?.id) === String(selection.subcategoryId),
      ) ||
      subcategories.find(
        (item) => String(item?.id) === String(selection.subcategoryId),
      ),
    [relatedSubcategories, selection.subcategoryId, subcategories],
  );

  const selectedCategoryLabel =
    selection.categoryName ||
    categoryProductsResponse?.category?.name ||
    displayProducts[0]?.categoryLabel ||
    categoryRecord?.name ||
    "Category Products";

  const selectedSubcategoryLabel =
    selection.type === "subcategory"
      ? selection.subcategoryName ||
        subcategoryProductsData?.name ||
        displayProducts[0]?.subcategoryLabel ||
        currentSubcategoryRecord?.name ||
        "Sub-category"
      : "";

  const pageTitle = selectedSubcategoryLabel || selectedCategoryLabel;

  const brandOptions = useMemo(() => {
    const map = new Map();

    displayProducts.forEach((product) => {
      if (!product.brandKey || !product.brandLabel) {
        return;
      }

      if (!map.has(product.brandKey)) {
        map.set(product.brandKey, product.brandLabel);
      }
    });

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [displayProducts]);

  const subcategoryOptions = useMemo(() => {
    const map = new Map();

    displayProducts.forEach((product) => {
      if (!product.subcategoryKey || !product.subcategoryLabel) {
        return;
      }

      if (!map.has(product.subcategoryKey)) {
        map.set(product.subcategoryKey, product.subcategoryLabel);
      }
    });

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [displayProducts]);

  const { minPrice, maxPrice } = useMemo(() => {
    if (!displayProducts.length) {
      return { minPrice: 0, maxPrice: 0 };
    }

    const prices = displayProducts.map((product) => cleanNumber(product.price));

    return {
      minPrice: Math.floor(Math.min(...prices)),
      maxPrice: Math.ceil(Math.max(...prices)),
    };
  }, [displayProducts]);

  useEffect(() => {
    setPriceRange([minPrice, maxPrice]);
  }, [
    maxPrice,
    minPrice,
    selection.categoryId,
    selection.subcategoryId,
    selection.type,
  ]);

  const activeMinPrice = priceRange ? priceRange[0] : minPrice;
  const activeMaxPrice = priceRange ? priceRange[1] : maxPrice;

  const filteredProducts = useMemo(() => {
    let results = displayProducts.filter((product) => {
      const price = cleanNumber(product.price);
      return price >= activeMinPrice && price <= activeMaxPrice;
    });

    if (selectedBrands.length) {
      results = results.filter((product) =>
        selectedBrands.includes(product.brandKey),
      );
    }

    if (selectedSubcategories.length) {
      results = results.filter((product) =>
        selectedSubcategories.includes(product.subcategoryKey),
      );
    }

    if (availability === "in") {
      results = results.filter((product) => !product.isOutOfStock);
    } else if (availability === "out") {
      results = results.filter((product) => product.isOutOfStock);
    }

    if (sortBy === "featured") {
      results = [...results].sort(
        (first, second) => Number(second.featured || 0) - Number(first.featured || 0),
      );
    } else if (sortBy === "nameAsc" || sortBy === "nameDesc") {
      const direction = sortBy === "nameAsc" ? 1 : -1;
      results = [...results].sort((first, second) =>
        direction * String(first.title || "").localeCompare(String(second.title || ""), "en", { sensitivity: "base" }),
      );
    } else if (sortBy === "priceLow") {
      results = [...results].sort(
        (first, second) => cleanNumber(first.price) - cleanNumber(second.price),
      );
    } else if (sortBy === "priceHigh") {
      results = [...results].sort(
        (first, second) => cleanNumber(second.price) - cleanNumber(first.price),
      );
    } else {
      const direction = sortBy === "oldest" ? 1 : -1;
      results = [...results].sort((first, second) => {
        const firstValue = first.createdAtValue || Number(first.id) || 0;
        const secondValue = second.createdAtValue || Number(second.id) || 0;
        return direction * (firstValue - secondValue);
      });
    }

    return results;
  }, [
    activeMaxPrice,
    activeMinPrice,
    availability,
    displayProducts,
    selectedBrands,
    selectedSubcategories,
    sortBy,
  ]);

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const safeCurrentPage =
    totalPages > 0 && currentPage > totalPages ? 1 : currentPage;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeMaxPrice,
    activeMinPrice,
    availability,
    selectedBrands,
    selectedSubcategories,
    sortBy,
  ]);

  useEffect(() => {
    if (totalPages === 0) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!hasMountedPageRef.current) {
      hasMountedPageRef.current = true;
      return;
    }

    productsTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [currentPage]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, safeCurrentPage]);

  const hasActiveFilters =
    selectedBrands.length > 0 ||
    selectedSubcategories.length > 0 ||
    availability !== "all" ||
    sortBy !== "newest" ||
    activeMinPrice !== minPrice ||
    activeMaxPrice !== maxPrice;

  const resetFilters = () => {
    setSelectedBrands([]);
    setSelectedSubcategories([]);
    setAvailability("all");
    updateMobileSort("newest");
    setPriceRange([minPrice, maxPrice]);
    setCurrentPage(1);
  };

  const onAddToCart = (product) => {
    const payload = {
      product_id: product?.id,
      quantity: 1,
      price: Number(product?.basePrice || product?.price || 0),
      discount: Number(product?.discount || 0),
      attributes: product?.attributes || {},
    };

    if (!payload.product_id) {
      return;
    }

    if (!getBuyerToken()) {
      addGuestCartItem(payload);
      showSuccess("Cart Added Successfully");
      return;
    }

    insertCartRequest(payload);
  };

  const navigateToCategoryProducts = ({
    type,
    categoryId,
    categoryName,
    subcategoryId,
    subcategoryName,
  }) => {
    const search = buildCategoryProductsSearch({
      type,
      categoryId,
      categoryName,
      subcategoryId,
      subcategoryName,
      sortBy: window.innerWidth <= 768 ? sortBy : undefined,
    });

    navigate(
      {
        pathname: "/categoryproducts",
        search,
      },
      {
        state:
          type === "subcategory"
            ? {
                id: subcategoryId,
                type: "subcategory",
                catparentid: categoryId,
                categoryName,
                subcategoryName,
              }
            : {
                id: categoryId,
                categoryName,
              },
      },
    );
  };

  const isLoading =
    selection.type === "subcategory" ? subcategoryLoading : categoryLoading;
  const fetchError =
    selection.type === "subcategory" ? subcategoryError : categoryError;

  const retryProducts = () => {
    if (selection.type === "subcategory" && selection.subcategoryId) {
      getsubcategorywithproductRequest(selection.subcategoryId);
    } else if (selection.categoryId) {
      getproductbycategoryidRequest(selection.categoryId);
    }
  };

  if (isLoading && !displayProducts.length) {
    return <Loading />;
  }

  return (
    <div className="cp-page" data-view={mobileDrawer.viewMode}>
      <div className="cp-shell">
        <nav className="ds-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="sep">{">"}</span>
          <span>Categories</span>
          <span className="sep">{">"}</span>
          <span className={selection.type === "subcategory" ? "" : "current"}>
            {selectedCategoryLabel}
          </span>
          {selection.type === "subcategory" ? (
            <>
              <span className="sep">{">"}</span>
              <span className="current">{selectedSubcategoryLabel}</span>
            </>
          ) : null}
        </nav>

        {relatedSubcategories.length > 0 ? (
          <div className="cp-quick-links" aria-label="Sub-category shortcuts">
            <button
              type="button"
              className={
                selection.type === "category"
                  ? "cp-quick-link is-active"
                  : "cp-quick-link"
              }
              onClick={() =>
                navigateToCategoryProducts({
                  type: "category",
                  categoryId: selection.categoryId,
                  categoryName: selectedCategoryLabel,
                })
              }
            >
              All Products
            </button>

            {relatedSubcategories.map((subcategory) => (
              <button
                key={subcategory.id}
                type="button"
                className={
                  String(subcategory.id) === String(selection.subcategoryId)
                    ? "cp-quick-link is-active"
                    : "cp-quick-link"
                }
                onClick={() =>
                  navigateToCategoryProducts({
                    type: "subcategory",
                    categoryId: selection.categoryId,
                    categoryName: selectedCategoryLabel,
                    subcategoryId: subcategory.id,
                    subcategoryName: subcategory.name,
                  })
                }
              >
                {subcategory.name}
              </button>
            ))}
          </div>
        ) : null}

        {/* <section className="cp-summary-card">
          <div>
            <p className="cp-kicker">Category Products</p>
            <h1 className="cp-title">{pageTitle}</h1>
            <p className="cp-subtitle">
              Showing {filteredProducts.length} of {displayProducts.length}{" "}
              products
              {selection.type === "subcategory" && selectedCategoryLabel
                ? ` in ${selectedCategoryLabel}`
                : ""}
              .
            </p>
          </div>

          <div className="cp-summary-meta">
            <span className="cp-count-pill">
              {filteredProducts.length} items
            </span>
          </div>
        </section> */}

        <MobileCollectionControls title={pageTitle} count={filteredProducts.length} sortBy={sortBy} onSort={updateMobileSort} drawer={mobileDrawer} supportsDateSort={supportsDateSort} supportsFeaturedSort={supportsFeaturedSort} />
        {mobileDrawer.filtersOpen && <button type="button" className="cp-mobile-backdrop" aria-label="Close filters" onClick={() => mobileDrawer.setFiltersOpen(false)} />}
        <div className="cp-layout">
          <aside ref={mobileDrawer.drawerRef} className={`cp-sidebar ${mobileDrawer.filtersOpen ? "is-open" : ""}`} role={mobileDrawer.filtersOpen ? "dialog" : undefined} aria-modal={mobileDrawer.filtersOpen ? "true" : undefined} aria-label="Product filters">
            <div className="cp-filter-card">
              <div className="cp-filter-header">
                <MobileFilterClose onClose={() => mobileDrawer.setFiltersOpen(false)} />
                <div>
                  <p className="cp-filter-kicker">Filters</p>
                  <h2 className="cp-filter-title">Refine results</h2>
                </div>

                <button
                  type="button"
                  className="cp-reset-button"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>

              <div className="cp-filter-section">
                <h3>Category</h3>
                <div className="cp-static-filter">{selectedCategoryLabel}</div>
              </div>

              <div className="cp-filter-section">
                <h3>Sub-category</h3>
                {subcategoryOptions.length > 0 ? (
                  <div className="cp-option-list">
                    {subcategoryOptions.map((option) => (
                      <label className="cp-option" key={option.value}>
                        <input
                          type="checkbox"
                          checked={selectedSubcategories.includes(option.value)}
                          onChange={() =>
                            setSelectedSubcategories((currentValues) =>
                              toggleValue(currentValues, option.value),
                            )
                          }
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-empty-filter">No sub-categories available</p>
                )}
              </div>

              <div className="cp-filter-section">
                <h3>Brand</h3>
                {brandOptions.length > 0 ? (
                  <div className="cp-option-list">
                    {brandOptions.map((option) => (
                      <label className="cp-option" key={option.value}>
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(option.value)}
                          onChange={() =>
                            setSelectedBrands((currentValues) =>
                              toggleValue(currentValues, option.value),
                            )
                          }
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-empty-filter">No brands available</p>
                )}
              </div>

              <div className="cp-filter-section">
                <h3>Price range</h3>
                <div className="cp-price-grid">
                  <label className="cp-price-field">
                    <span>Min Price</span>
                    <input
                      type="number"
                      min={0}
                      value={activeMinPrice}
                      onChange={(event) => {
                        const nextValue = Math.max(
                          0,
                          Number(event.target.value) || 0,
                        );

                        setPriceRange([
                          Math.min(nextValue, activeMaxPrice),
                          activeMaxPrice,
                        ]);
                      }}
                    />
                  </label>

                  <label className="cp-price-field">
                    <span>Max Price</span>
                    <input
                      type="number"
                      min={0}
                      value={activeMaxPrice}
                      onChange={(event) => {
                        const nextValue = Math.max(
                          0,
                          Number(event.target.value) || 0,
                        );

                        setPriceRange([
                          Math.min(activeMinPrice, nextValue),
                          Math.max(activeMinPrice, nextValue),
                        ]);
                      }}
                    />
                  </label>
                </div>

                <p className="cp-filter-note">
                  Range: AED {minPrice} to AED {maxPrice}
                </p>
              </div>

              <div className="cp-filter-section">
                <h3>Availability</h3>
                <div className="cp-option-list">
                  <label className="cp-option">
                    <input
                      type="radio"
                      name="availability"
                      checked={availability === "all"}
                      onChange={() => setAvailability("all")}
                    />
                    <span>All Products</span>
                  </label>
                  <label className="cp-option">
                    <input
                      type="radio"
                      name="availability"
                      checked={availability === "in"}
                      onChange={() => setAvailability("in")}
                    />
                    <span>In Stock</span>
                  </label>
                  <label className="cp-option">
                    <input
                      type="radio"
                      name="availability"
                      checked={availability === "out"}
                      onChange={() => setAvailability("out")}
                    />
                    <span>Out of Stock</span>
                  </label>
                </div>
              </div>

              <div className="cp-filter-section">
                <h3>Sort by</h3>
                <div className="cp-option-list">
                  {SORT_OPTIONS.map((option) => (
                    <label className="cp-option" key={option.value}>
                      <input
                        type="radio"
                        name="sortBy"
                        checked={sortBy === option.value}
                        onChange={() => setSortBy(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <MobileFilterActions onClose={() => mobileDrawer.setFiltersOpen(false)} />
            </div>
          </aside>

          <main className="cp-main">
            <div className="cp-results-card" ref={productsTopRef}>
              {filteredProducts.length > 0 ? (
                <>
                <div className="cp-products-grid">
                  {paginatedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      id={product.id}
                      title={product.title}
                      imageUrl={product.imageUrl}
                      price={product.price}
                      oldPrice={product.oldPrice}
                      discount={product.discount}
                      tagText={null}
                      description={product.description}
                      onAdd={() => onAddToCart(product)}
                      category_id={product.category_id}
                      company_id={product.company_id}
                      featured={product.featured}
                      status={product.status}
                      stock_quantity={product.stock_quantity}
                      item={product}
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={safeCurrentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
                </>
              ) : (
                <div className={`cp-empty-state ${fetchError ? "has-error" : ""}`}>
                  {fetchError ? (
                    <div className="cp-mobile-error" role="alert">
                      <h3>Products could not be loaded</h3>
                      <p>Please try again in a moment.</p>
                      <button type="button" className="cp-empty-action" onClick={retryProducts}>Try again</button>
                    </div>
                  ) : null}
                  <h3 className="cp-empty-default">No products found</h3>
                  <p className="cp-empty-default">
                    Try adjusting your filters or reset them to explore more
                    available products in this category.
                  </p>
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      className="cp-empty-action cp-empty-default"
                      onClick={resetFilters}
                    >
                      Reset Filters
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  addcartdata: state.Home.Cartdata,
  categoryProductsResponse: state.Home.getproductbycategoryiddata,
  categoryLoading: state.Home.getproductbycategoryidLoading,
  categoryError: state.Home.getproductbycategoryidError,
  categorydata: state.Product.categorydata?.data || [],
  subcategories:
    getCustomerSubcategories(state.Home.getcategorywithsubcategorydata?.data) ||
    (CATALOG_MENU_MODE === "legacy" ? [] : state.Category.getSubcategorydata?.data || []),
  hasCustomerCategoryTree: Array.isArray(state.Home.getcategorywithsubcategorydata?.data),
  subcategoryLoading: state.Home.getsubcategorywithproductLoading,
  subcategoryError: state.Home.getsubcategorywithproducterror,
  subcategoryProductsData:
    state.Home.getsubcategorywithproductdata?.data ||
    state.Home.getsubcategorywithproductdata,
});

const mapDispatchToProps = (dispatch) => ({
  getcategoryRequest: () => dispatch(getcategoryRequest()),
  getCartRequest: () => dispatch(getCartRequest()),
  getproductbycategoryidRequest: (categoryId) =>
    dispatch(getproductbycategoryidRequest(categoryId)),
  getsubcategoryRequest: () => dispatch(getSubcategoryRequest()),
  getcategorywithsubcategoryRequest: () => dispatch(getcategorywithsubcategoryRequest()),
  getsubcategorywithproductRequest: (subcategoryId) =>
    dispatch(getsubcategorywithproductRequest(subcategoryId)),
  insertCartRequest: (payload) => dispatch(insertCartRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CategoryProducts);
